import {gaConfig} from '@stencil/hotels-config';
import {
  updateUgcImages,
  updateSafetyHygieneMediaData,
} from '@stencil/hotels-details-reducer/src/actions';
// SELECTORS
import {
  captivateGallery as captivateGallerySelectorFn,
  hotelIdSelector,
  hotelierGallery as hotelierGallerySelectorFn,
  totalTravellerImages as totalImagesSelectorFn,
  travellerGallery as travellerGallerySelectorFn,
  goSafeSegmentDataSelectorFn,
  safetyHygieneMediaDataSelector,
} from '@stencil/hotels-details-reducer/src/selectors';
// TYPES
import {IGallery} from '@stencil/hotels-details-reducer/src/types';
import detailsPageClickGaPush from '@stencil/hotels-utils/src/details-page-click-ga-push';
import {detailsNavigationIdsAndValues} from '@stencil/hotels-utils/src/get-details-navigation-id-and-values';
// UTILS
import {scrollToEl} from '@stencil/hotels-utils/src/scroll-into-view-details';
import React, {useCallback, useEffect, useState, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import ClientOnly from '@stencil/generic-client-only-component';
import Modal, {useModal} from '@stencil/hotels-modal-component';
import fallbackImage from '../../../../../stories/assets/fallback-image.png';
import MainGallery from '../../../../../stories/components/hotels/DetailsPage/MainGallery/MainGallery';
import TravellerPhotoUpload from '../../../../../stories/components/hotels/DetailsPage/TravellerPhotoUpload/TravellerPhotoUpload';
// COMPONENTS
import ImageGalleryPopup from '../../../../../stories/components/hotels/SRP/ImageGalleryPopup/ImageGalleryPopup';
import LocationInfoIcon from '../../../../../stories/Icons/LocationInfoIcon';
import TriggerActionText from '../../../../components/TriggerActionText';
import {AppState} from '../../../../store';
import {updateSelectedPlaces} from '../../../HotelsMap/redux/actions';
import {GoogleMapLocationType} from '../../../HotelsMap/redux/types';
import {PrimaryLink} from '../../../../../stories/components/CommonStyleUtils/dwebCommon.styles';
//ACTIONS
import {fetchMoreImages} from '@stencil/hotels-ugc-redux/src/hotels-ugc-thunks';
import {fetchMediaImages} from '@stencil/hotels-details-reducer/src/thunks';
// STYLES
import * as S from './styles/GalleriesAndLocationPersuasion.styles';

const {DetailsGAClickEventActions} = gaConfig;

interface IPersuationType {
  name?: string;
  value?: string;
  coordinates?: Array<number | null>;
  _id?: string;
}

const GalleriesAndLocationPersuasion: React.FC<{
  locationPersuasions: IPersuationType[];
}> = ({locationPersuasions = []}) => {
  const hotelierImages = useSelector(hotelierGallerySelectorFn);
  const travellerImages = useSelector(travellerGallerySelectorFn);
  const captivateImages = useSelector(captivateGallerySelectorFn);
  const safetyHygieneAllMediaData = useSelector(safetyHygieneMediaDataSelector);
  const {goSafeCaptivateImages = {}} = useSelector(goSafeSegmentDataSelectorFn);
  const {
    media: safeHygieneImages = [],
    title: safeHygienceTitle = '',
    count: safeHygienceCount = 0,
    id: tagId,
  } = goSafeCaptivateImages;

  const hotelId = useSelector(hotelIdSelector);
  const moreUgcImages = useSelector((state: AppState) => state?.ugc?.moreUgcImages || []);
  const totalTravellerImagesCount = useSelector(totalImagesSelectorFn);
  const dispatch = useDispatch();

  const [typeOfGalleryPopup, updateTypeOfGalleryPopup] = useState('');
  const [totalUgcImages, updateTotalUgcImages] = useState(totalTravellerImagesCount);
  const captivateImagesCount = captivateImages.length;

  const getAllSafetyMediaImages = () => {
    if (!safetyHygieneAllMediaData.length) dispatch(fetchMediaImages(tagId));
  };

  let formattedLocationPersuasions: {
    n?: string;
    value?: string;
    crd?: Array<number | null>;
    _id?: string;
    type?: string;
  } = {};

  if (!!locationPersuasions.length) {
    formattedLocationPersuasions = {
      n: locationPersuasions[0]?.name || '',
      value: locationPersuasions[0]?.value || '',
      crd: [locationPersuasions[0]?.coordinates[1], locationPersuasions[0]?.coordinates[0]] || [],
      _id: locationPersuasions[0]?._id || '',
      type: GoogleMapLocationType.attraction,
    };
  }

  const setSelectedPlace = place => dispatch(updateSelectedPlaces(place));

  const viewOnMap = () => {
    detailsPageClickGaPush({
      eventAction: DetailsGAClickEventActions.locationInfoViewInMapClick,
    });

    if (!!formattedLocationPersuasions?.crd?.length) {
      setSelectedPlace(formattedLocationPersuasions);
      scrollToEl(detailsNavigationIdsAndValues.location.id);
      return;
    }
    scrollToEl(detailsNavigationIdsAndValues.location.id);
  };

  const updateMoreUgcImages = useCallback(
    (vid, offset, lim) => {
      return dispatch(fetchMoreImages(vid, offset, lim));
    },
    [moreUgcImages],
  );

  let placeholderHotelierGalleryImage = '',
    placeholderTravellerGalleryImage = '',
    placeholderSafetyHygieneImage = '';

  placeholderHotelierGalleryImage = (hotelierImages[0] || [])[2];
  if ((captivateImages[0] || {}).m === (hotelierImages[0] || [])[1]) {
    placeholderHotelierGalleryImage = (hotelierImages[1] || [])[2];
  }

  placeholderTravellerGalleryImage = (travellerImages[0] || []).b;
  if ((captivateImages[0] || {}).b === (travellerImages[0] || {}).b) {
    placeholderTravellerGalleryImage = (travellerImages[1] || {}).b;
  }

  placeholderSafetyHygieneImage =
    (!!safeHygieneImages.length &&
      (safeHygieneImages[0]?.thumb_url ||
        safeHygieneImages[0]?.processedVideos?.snapshotUrl ||
        '')) ||
    '';

  const getMoreData = offset => {
    updateMoreUgcImages(hotelId, offset, 50);
    if (moreUgcImages.length === 0 && offset !== 0) {
      updateTotalUgcImages(travellerImages.length);
    }
  };

  useEffect(() => {
    let moreImages = [];

    moreUgcImages.map(item => {
      let ugcImage = {
        s: item.mobileThumbUrl,
        m: item.thumbUrl,
        b: item.bigUrl,
        tag: item.tags[0],
      };
      moreImages.push(ugcImage);
    });
    dispatch(updateUgcImages(travellerImages.concat(moreImages)));
    return () => {
      dispatch(updateSafetyHygieneMediaData([]));
    };
  }, [moreUgcImages]);

  const [totalHotelierImages, setHotelImages] = useState<Array<IGallery>>([]);
  const [totalTravellerImages, setTravellerImages] = useState<Array<IGallery>>([]);

  const changeTypeOfGalleryPopup = type => {
    updateTypeOfGalleryPopup(type);
  };

  const getGalleryData = () => {
    let htl_capt: Array<IGallery> = captivateImages.filter(item => item.src == 'ing');
    let ugc_capt: Array<IGallery> = captivateImages.filter(item => item.src == 'ugc');

    if (htl_capt.length > 0) {
      hotelierImages.map(item => {
        let dupHotelImg = htl_capt.filter(i => i.m === item[1]);

        if (dupHotelImg.length === 0) {
          let hotellierImg = {s: item[0], m: item[1], b: item[2], tag: item[5]};
          htl_capt.push(hotellierImg);
        }
      });
    } else {
      hotelierImages.map(item => {
        let hotellierImg = {s: item[0], m: item[1], b: item[2], tag: item[5]};
        htl_capt.push(hotellierImg);
      });
    }

    if (ugc_capt.length > 0) {
      travellerImages.map(item => {
        let dupTravImg = ugc_capt.filter(i => i.m === item.m);
        if (dupTravImg.length === 0) {
          let travellerImg = {s: item.s, m: item.m, b: item.b, tag: item.tag || item.t};
          ugc_capt.push(travellerImg);
        }
      });
    } else {
      travellerImages.map(item => {
        let travellerImg = {s: item.s, m: item.m, b: item.b, tag: item.tag || item.t};
        ugc_capt.push(travellerImg);
      });
    }

    let galleryData = {
      hotelierData: htl_capt,
      travellerData: ugc_capt,
    };
    return galleryData;
  };

  useEffect(() => {
    const galleryData = getGalleryData();
    const {hotelierData, travellerData} = galleryData;
    setHotelImages(hotelierData);
    setTravellerImages(travellerData);
  }, [travellerImages]);

  const transformedSafeHygienceImages = useMemo(() => {
    let dataUsed =
      (!!safetyHygieneAllMediaData.length && safetyHygieneAllMediaData) || safeHygieneImages;
    const transformedData = dataUsed.map(
      ({gallery_url = '', thumb_url = '', processedVideos = {snapshotUrl: ''}}) => {
        const {snapshotUrl = ''} = processedVideos;
        return {b: gallery_url || snapshotUrl, m: thumb_url || snapshotUrl, tag: ''};
      },
    );
    return transformedData;
  }, [safeHygieneImages, safetyHygieneAllMediaData]);

  const hotellierImagesCount = totalHotelierImages.length;

  const {
    isModalOpen: isGalleryModalOpen,
    closeModal: closeGalleryModal,
    openModal: openGalleryModal,
  } = useModal();

  return (
    <>
      <Modal
        isOpen={isGalleryModalOpen}
        closeModal={closeGalleryModal}
        modalStyles={{width: '103.4rem'}}
      >
        {totalUgcImages > 0 && typeOfGalleryPopup === 'traveller' && (
          <ImageGalleryPopup
            data={totalTravellerImages}
            getMoreData={getMoreData}
            totalCount={totalUgcImages}
            typeOfPopup={'traveller'}
            changeTypeOfGalleryPopup={changeTypeOfGalleryPopup}
            // tabsRequired={hotellierImagesCount > 0 ? true : false}

            travellerTab={totalUgcImages > 0}
            hotellierTab={hotellierImagesCount > 0 || captivateImagesCount > 0}
            safetyTab={safeHygienceCount > 0}
          />
        )}
        {/* {captivateImagesCount > 0 && typeOfGalleryPopup === 'hotellier' && (
          <ImageGalleryPopup
            data={captivateImages}
            totalCount={captivateImagesCount}
            typeOfPopup={'hotellier'}
            changeTypeOfGalleryPopup={changeTypeOfGalleryPopup}
            // tabsRequired={totalUgcImages > 0 ? true : false}
            travellerTab={totalUgcImages > 0}
            hotellierTab={hotellierImagesCount > 0 || captivateImagesCount > 0}
            safetyTab={safeHygienceCount > 0}
          />
        )} */}
        {(hotellierImagesCount > 0 || captivateImagesCount > 0) &&
          typeOfGalleryPopup === 'hotellier' && (
            <ImageGalleryPopup
              data={hotellierImagesCount > 0 ? totalHotelierImages : captivateImages}
              totalCount={hotellierImagesCount > 0 ? hotellierImagesCount : captivateImagesCount}
              typeOfPopup={'hotellier'}
              changeTypeOfGalleryPopup={changeTypeOfGalleryPopup}
              travellerTab={totalUgcImages > 0}
              hotellierTab={hotellierImagesCount > 0 || captivateImagesCount > 0}
              safetyTab={safeHygienceCount > 0}
            />
          )}

        {safeHygienceCount > 0 && typeOfGalleryPopup === 'goSafe' && (
          <ImageGalleryPopup
            data={transformedSafeHygienceImages}
            totalCount={safeHygienceCount}
            typeOfPopup={'goSafe'}
            changeTypeOfGalleryPopup={changeTypeOfGalleryPopup}
            getMoreData={getAllSafetyMediaImages}
            // tabsRequired={true}
            travellerTab={totalUgcImages > 0}
            hotellierTab={hotellierImagesCount > 0 || captivateImagesCount > 0}
            safetyTab={safeHygienceCount > 0}
          />
        )}
      </Modal>
      <S.GalleryBlockContainer>
        {placeholderHotelierGalleryImage && placeholderTravellerGalleryImage ? (
          <>
            <MainGallery
              alternateText={'Hotellier gallery placeholder'}
              dimension={'normalWidth'}
              data={captivateImages}
              onGalleryClick={() => {
                detailsPageClickGaPush({
                  eventAction: DetailsGAClickEventActions.bigHotelImageGalleryOpenerClick,
                });
                openGalleryModal();
                updateTypeOfGalleryPopup('hotellier');
              }}
            />

            <S.SmallGalleriesAndLocationPersuasion>
              {!!hotellierImagesCount && (
                <S.GalleryContainer
                  onClick={() => {
                    detailsPageClickGaPush({
                      eventAction: DetailsGAClickEventActions.hotelGalleryOpenerClick,
                    });

                    openGalleryModal();
                    updateTypeOfGalleryPopup('hotellier');
                  }}
                >
                  <TravellerPhotoUpload
                    source={placeholderHotelierGalleryImage}
                    text={'HOTELIER'}
                    alternateText={'hotelier gallery placeholder'}
                    noOfPhotos={hotellierImagesCount}
                  />
                </S.GalleryContainer>
              )}
              {!!totalUgcImages && (
                <S.GalleryContainer
                  onClick={() => {
                    detailsPageClickGaPush({
                      eventAction: DetailsGAClickEventActions.guestGalleryOpenerClick,
                    });

                    openGalleryModal();
                    updateTypeOfGalleryPopup('traveller');
                  }}
                >
                  <TravellerPhotoUpload
                    source={placeholderTravellerGalleryImage}
                    text={'TRAVELLER'}
                    alternateText={'traveller gallery placeholder'}
                    noOfPhotos={totalUgcImages}
                  />
                </S.GalleryContainer>
              )}
              {(placeholderSafetyHygieneImage && !!safeHygienceCount && (
                <S.GalleryContainer
                  onClick={() => {
                    detailsPageClickGaPush({
                      eventAction: DetailsGAClickEventActions.guestGalleryOpenerClick,
                    });

                    openGalleryModal();
                    updateTypeOfGalleryPopup('goSafe');
                  }}
                >
                  <TravellerPhotoUpload
                    source={placeholderSafetyHygieneImage}
                    text={safeHygienceTitle || 'Safety and Hygiene'}
                    alternateText={'Safety gallery placeholder'}
                    noOfPhotos={safeHygienceCount}
                  />
                </S.GalleryContainer>
              )) || (
                <S.LocationInfo>
                  <S.LocationInfoHeaderWrap>
                    <LocationInfoIcon />
                    <S.LocationHeader>Location Info</S.LocationHeader>
                  </S.LocationInfoHeaderWrap>
                  <S.LocationInfoTextContainer>
                    {!!formattedLocationPersuasions.value && (
                      <S.DistanceList>
                        <S.LocationInfoItem onClick={() => viewOnMap()}>
                          {formattedLocationPersuasions.value}
                        </S.LocationInfoItem>
                      </S.DistanceList>
                    )}
                    <S.ViewInMap noLocationItems={!formattedLocationPersuasions.value}>
                      <PrimaryLink onClick={() => viewOnMap()}>View on Map</PrimaryLink>
                    </S.ViewInMap>
                  </S.LocationInfoTextContainer>
                </S.LocationInfo>
              )}
            </S.SmallGalleriesAndLocationPersuasion>
          </>
        ) : (
          ''
        )}
        {!(placeholderHotelierGalleryImage && placeholderTravellerGalleryImage) &&
        captivateImages.length > 0 ? (
          <MainGallery
            alternateText={'Hotellier gallery placeholder'}
            dimension={'fullWidth'}
            data={captivateImages}
            onGalleryClick={() => {
              openGalleryModal();
              updateTypeOfGalleryPopup('hotellier');
            }}
          />
        ) : (
          ''
        )}
        {!placeholderHotelierGalleryImage &&
        captivateImages.length <= 0 &&
        placeholderTravellerGalleryImage ? (
          <MainGallery
            alternateText={'Hotellier gallery placeholder'}
            dimension={'fullWidth'}
            data={totalTravellerImages}
            onGalleryClick={() => {
              openGalleryModal();
              updateTypeOfGalleryPopup('traveller');
            }}
          />
        ) : (
          ''
        )}
        {!placeholderTravellerGalleryImage &&
        captivateImages.length <= 0 &&
        placeholderHotelierGalleryImage ? (
          <MainGallery
            alternateText={'Hotellier gallery placeholder'}
            dimension={'fullWidth'}
            data={totalHotelierImages}
            onGalleryClick={() => {
              openGalleryModal();
              updateTypeOfGalleryPopup('hotellier');
            }}
          />
        ) : (
          ''
        )}
        {!placeholderTravellerGalleryImage &&
        !placeholderHotelierGalleryImage &&
        !(captivateImages.length > 0) ? (
          <S.NoGalleryImageWrap>
            <ClientOnly>
              <S.ImageStyle src={fallbackImage} />
            </ClientOnly>
          </S.NoGalleryImageWrap>
        ) : (
          ''
        )}
      </S.GalleryBlockContainer>
    </>
  );
};

export default GalleriesAndLocationPersuasion;
